import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, 
  UserGroupIcon, 
  TrophyIcon, 
  FireIcon, 
  CalendarDaysIcon,
  ClockIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const MemberProgress = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');

  // Mock data
  const mockMembers = [
    {
      id: 1,
      name: 'Ahmed Benali',
      plan: 'Programme Débutant',
      startDate: '2024-01-15',
      sessionsCompleted: 24,
      totalSessions: 36,
      currentWeight: 75,
      startWeight: 82,
      nextSession: '2024-02-01',
      achievements: ['Premier mois', 'Régularité'],
      progressData: [
        { date: '2024-01-15', weight: 82, performance: 60 },
        { date: '2024-01-22', weight: 81, performance: 65 },
        { date: '2024-01-29', weight: 79, performance: 70 },
        { date: '2024-02-05', weight: 77, performance: 75 },
        { date: '2024-02-12', weight: 75, performance: 80 }
      ]
    },
    {
      id: 2,
      name: 'Fatima Zahra',
      plan: 'Cardio Intensif',
      startDate: '2024-01-10',
      sessionsCompleted: 18,
      totalSessions: 30,
      currentWeight: 65,
      startWeight: 68,
      nextSession: '2024-02-02',
      achievements: ['Perte de poids', 'Endurance'],
      progressData: [
        { date: '2024-01-10', weight: 68, performance: 55 },
        { date: '2024-01-17', weight: 67, performance: 60 },
        { date: '2024-01-24', weight: 66, performance: 65 },
        { date: '2024-01-31', weight: 65, performance: 72 },
        { date: '2024-02-07', weight: 65, performance: 78 }
      ]
    },
    {
      id: 3,
      name: 'Omar Mansouri',
      plan: 'Force et Masse',
      startDate: '2024-01-08',
      sessionsCompleted: 32,
      totalSessions: 32,
      currentWeight: 85,
      startWeight: 78,
      nextSession: '2024-02-03',
      achievements: ['Expert', 'Gain de masse', 'Régularité'],
      progressData: [
        { date: '2024-01-08', weight: 78, performance: 70 },
        { date: '2024-01-15', weight: 79, performance: 75 },
        { date: '2024-01-22', weight: 81, performance: 80 },
        { date: '2024-01-29', weight: 83, performance: 85 },
        { date: '2024-02-05', weight: 85, performance: 90 }
      ]
    }
  ];

  const filteredMembers = mockMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = planFilter === 'all' || member.plan === planFilter;
    return matchesSearch && matchesPlan;
  });

  const getProgressPercentage = (completed, total) => {
    return Math.round((completed / total) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'text-green-400';
    if (percentage >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getWeightChange = (current, start) => {
    const change = current - start;
    return {
      value: Math.abs(change),
      direction: change > 0 ? 'gain' : 'loss',
      color: change > 0 ? 'text-blue-400' : 'text-green-400'
    };
  };

  const globalStats = {
    totalMembers: mockMembers.length,
    activeMembers: mockMembers.filter(m => getProgressPercentage(m.sessionsCompleted, m.totalSessions) < 100).length,
    completedPrograms: mockMembers.filter(m => getProgressPercentage(m.sessionsCompleted, m.totalSessions) === 100).length,
    avgProgress: Math.round(mockMembers.reduce((acc, m) => acc + getProgressPercentage(m.sessionsCompleted, m.totalSessions), 0) / mockMembers.length)
  };

  return (
    <div className="space-y-6">
      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Membres suivis</p>
              <p className="text-2xl font-bold text-white">{globalStats.totalMembers}</p>
            </div>
            <UserGroupIcon className="h-8 w-8 text-blue-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">En cours</p>
              <p className="text-2xl font-bold text-white">{globalStats.activeMembers}</p>
            </div>
            <FireIcon className="h-8 w-8 text-orange-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Terminés</p>
              <p className="text-2xl font-bold text-white">{globalStats.completedPrograms}</p>
            </div>
            <TrophyIcon className="h-8 w-8 text-yellow-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Progression moy.</p>
              <p className="text-2xl font-bold text-white">{globalStats.avgProgress}%</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-purple-400" />
          </div>
        </motion.div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
          <input
            type="text"
            placeholder="Rechercher un membre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-3">
          <FunnelIcon className="h-5 w-5 text-white/50" />
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all" className="bg-gray-800">Tous les plans</option>
            <option value="Programme Débutant" className="bg-gray-800">Programme Débutant</option>
            <option value="Cardio Intensif" className="bg-gray-800">Cardio Intensif</option>
            <option value="Force et Masse" className="bg-gray-800">Force et Masse</option>
          </select>
        </div>
      </div>

      {/* Liste des membres */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredMembers.map((member, index) => {
          const progressPercentage = getProgressPercentage(member.sessionsCompleted, member.totalSessions);
          const weightChange = getWeightChange(member.currentWeight, member.startWeight);
          
          return (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all duration-300"
            >
              {/* En-tête membre */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">{member.name.charAt(0)}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{member.name}</h3>
                    <p className="text-white/60 text-sm">{member.plan}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMember(member)}
                  className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Progression */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white/80 text-sm">Progression</span>
                  <span className={`text-sm font-medium ${getProgressColor(progressPercentage)}`}>
                    {progressPercentage}%
                  </span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      progressPercentage >= 80 ? 'bg-green-400' :
                      progressPercentage >= 60 ? 'bg-yellow-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <p className="text-white/60 text-xs mt-1">
                  {member.sessionsCompleted}/{member.totalSessions} séances
                </p>
              </div>

              {/* Statistiques */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-white font-semibold">{weightChange.value} kg</div>
                  <div className={`text-xs ${weightChange.color}`}>
                    {weightChange.direction === 'gain' ? '▲' : '▼'} Poids
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold">{member.achievements.length}</div>
                  <div className="text-white/60 text-xs">Badges</div>
                </div>
              </div>

              {/* Prochaine séance */}
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <CalendarDaysIcon className="h-4 w-4" />
                <span>Prochaine : {new Date(member.nextSession).toLocaleDateString('fr-FR')}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Modal de détails du membre */}
      {selectedMember && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMember(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Progression de {selectedMember.name}</h2>
              <button
                onClick={() => setSelectedMember(null)}
                className="text-white/60 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Graphique de progression */}
              <div className="bg-white/5 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Évolution du poids</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={selectedMember.progressData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke="rgba(255,255,255,0.6)" fontSize={12} />
                    <YAxis stroke="rgba(255,255,255,0.6)" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }} 
                    />
                    <Line type="monotone" dataKey="weight" stroke="#3B82F6" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Graphique de performance */}
              <div className="bg-white/5 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Performance</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={selectedMember.progressData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke="rgba(255,255,255,0.6)" fontSize={12} />
                    <YAxis stroke="rgba(255,255,255,0.6)" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }} 
                    />
                    <Bar dataKey="performance" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Badges et achievements */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Achievements</h3>
              <div className="flex flex-wrap gap-3">
                {selectedMember.achievements.map((achievement, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 rounded-full text-sm border border-yellow-500/30"
                  >
                    🏆 {achievement}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default MemberProgress; 